import React, { useState } from 'react';

import type { ToolbarButtons } from '../../../models';
import type { OverflownItemsPosition } from '../../../types';
import { MoreButton } from '../../buttons';
import { useSizeCalculator } from '../../UseSizeCalculator';
import { TOOLBAR_VISIBLE } from '../data-hooks';
import { useFocusHandler } from './FocusHandler/UseFocusHandler';
import { OverflownButtons } from './OverflownButtons';
import styles from './RicosDesktopToolbar-scss';
import { RicosDesktopToolbarWrapper } from './RicosDesktopToolbarWrapper';
import { useRicosDesktopToolbarActions } from './UseRicosDesktopToolbarActions';

export const RicosDesktopToolbar = ({
  type,
  maxWidth,
  toolbarButtons,
  onlyShownButtons,
  isRTL,
  overflownItemsPosition,
  isCircularFocusEnabled,
  alignment,
}: {
  type: 'floating' | 'static' | 'regular';
  maxWidth: number;
  toolbarButtons: ToolbarButtons;
  onlyShownButtons?: string[];
  isRTL: boolean;
  overflownItemsPosition?: OverflownItemsPosition;
  isCircularFocusEnabled?: boolean;
  alignment?: string;
}) => {
  const [moreButton, setMoreButton] = useState<HTMLButtonElement | null>(null);
  const showMoreAbove = overflownItemsPosition === 'top';

  const { shouldShowMore, onPressEscape, onClickOutside, toggleMoreItems } =
    useRicosDesktopToolbarActions();

  const { handleCircularFocus, handleMoreToolbarFocus } = useFocusHandler();

  const { data, OverflowCalculator } = useSizeCalculator({
    width: maxWidth,
    toolbarButtons,
    onlyShownButtons,
  });

  const wrapperProps = {
    type,
    isRTL,
    onPressEscape,
    onClickOutside,
    moreButton,
  };

  return (
    <RicosDesktopToolbarWrapper {...wrapperProps}>
      <OverflowCalculator />
      {!!data && (
        <div
          className={styles.toolbarVisible}
          data-hook={TOOLBAR_VISIBLE}
          role="toolbar"
          onKeyDown={e => {
            const args = {
              e,
              overflownButtons: data.overflownButtons,
              visibleButtons: data.visibleButtons,
              shouldShowMore,
              moreButton,
            };

            handleMoreToolbarFocus(args);

            if (isCircularFocusEnabled) {
              handleCircularFocus(args);
            }
          }}
        >
          {!!moreButton && (
            <OverflownButtons
              type={type}
              shouldShowMore={shouldShowMore}
              overflownButtons={data.overflownButtons}
              showMoreAbove={showMoreAbove}
              attachTo={moreButton}
            />
          )}
          {!data.visibleButtons.isEmpty() && (
            <div
              className={styles.visibleItems}
              style={{
                justifyContent: alignment,
              }}
            >
              {data.visibleButtons.getButtonsElementsWithDataHook()}
              {!data.overflownButtons.isEmpty() && (
                <MoreButton
                  ref={setMoreButton}
                  key="more-button"
                  onClick={toggleMoreItems}
                  shouldShowMore={shouldShowMore}
                />
              )}
            </div>
          )}
        </div>
      )}
    </RicosDesktopToolbarWrapper>
  );
};
